import React, { RefObject, useState } from "react"
import { UseFormRegister, UseFormSetValue } from "react-hook-form"
import { AnimatePresence } from "framer-motion"
import { styled } from "@mui/material/styles"
import { uploadImage } from "utils/componentsUtils"
import { AVATAR_UPDATED, DISCARD_IMAGE_CROP, SAVE_CROPPED_IMAGE, SET_ORIGINAL_IMAGE } from "constants/events"
import { CROP_SHAPE } from "types/general.types"
import useEventListener from "hooks/useEventListener"
import ImageCropperModal from "components/atoms/ImageCropperModal"

type ImageInputWithCropperModalProps = {
  id: string;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  containerRef?: RefObject<HTMLDivElement>;
  containerId?: string;
  children?: React.ReactNode;
  cropShape?: CROP_SHAPE;
  cropperAspectRatio?: number;
  isSubmitting?: boolean;
  isSingleForm?: boolean;
  setImage?: React.SetStateAction<any>;
  onChange: (value: File) => void;
}

export default function ImageInputWithCropperModal(props: ImageInputWithCropperModalProps) {
  const {
    id = "image",
    register,
    setValue,
    containerRef,
    containerId,
    children = null,
    cropperAspectRatio,
    cropShape,
    isSubmitting = false,
    isSingleForm = false,
    setImage = undefined,
    onChange
  } = props

  const [fileName, setFileName] = useState<string | null>(null)
  const [chosenImage, setChosenImage] = useState<string | null>(null)
  const [showCropModal, setShowCropModal] = useState<boolean>(false)

  useEventListener(document, SAVE_CROPPED_IMAGE, handleSaveCroppedImage)

  useEventListener(document, DISCARD_IMAGE_CROP, handleDiscardImageCrop)

  useEventListener(document, AVATAR_UPDATED, handleAvatarUpdated)

  function handleSaveCroppedImage(event: CustomEvent) {
    if (!fileName) return
    let container
    if (containerRef?.current) {
      container = containerRef?.current
    } else if (containerId) {
      container = document.getElementById(containerId)
    }
    if (!container) return
    const { blob } = event.detail
    const file = new File([blob], fileName, { type: "image/jpeg" })
    const customEventObj = { target: { files: [file] } }
    uploadImage(customEventObj, container, setImage, onChange)
    if (!isSingleForm) {
      setChosenImage(null)
      setShowCropModal(false)
    }
  }

  function handleDiscardImageCrop() {
    if (!showCropModal) return
    setFileName(null)
    setChosenImage(null)
    setShowCropModal(false)
    setValue(id, null)
  }

  function handleAvatarUpdated() {
    setChosenImage(null)
    setShowCropModal(false)
  }

  function handleSelectAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) {
      setFileName(file.name)
      const reader = new FileReader()
      reader.onload = () => {
        setChosenImage(reader.result as string)
        setShowCropModal(true)
        document.dispatchEvent(new CustomEvent(SET_ORIGINAL_IMAGE, { detail: { image: reader.result, filename: file.name } }))
      }
      reader.readAsDataURL(file)
    }
  }

  return (
    <>
      <ImageInputContainer className="image-input-container">
        <input
          id={id}
          type="file"
          accept="image/*"
          {...register(id)}
          onChange={handleSelectAvatar}
        />
        {children}
      </ImageInputContainer>

      <AnimatePresence>
        <ImageCropperModal
          show={showCropModal}
          loading={isSubmitting}
          aspectRatio={cropperAspectRatio}
          cropShape={cropShape}
          src={chosenImage}
        />
      </AnimatePresence>
    </>
  )
}

const ImageInputContainer = styled("label")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",

  "& input": {
    display: "none"
  }
}))
